import { makeRng, clamp } from '../core/Rand.js';

/**
 * COD-style recoil.
 *
 * The pattern is a *table*, not noise: the same seed and the same shot index always
 * produce the same kick, so a player can learn to pull down through a mag, and the
 * capture harness can reproduce a spray frame for frame. The random component is a
 * seeded jitter keyed on (seed, shotIndex), which means it does not depend on call
 * order, frame rate, or how many other weapons have fired.
 *
 * Visual recoil (what the viewmodel does) and aim recoil (what the camera does) are
 * separate curves. The gun moves roughly 2.5x as much as the view.
 */

/** 32-bit mix of a seed and a shot index — order independent. */
export function shotHash(seed, index) {
  let h = (seed ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (index + 0x85ebca6b), 0xcc9e2d51) >>> 0;
  h = ((h << 15) | (h >>> 17)) >>> 0;
  h = Math.imul(h, 0x1b873593) >>> 0;
  h ^= h >>> 16;
  return h >>> 0;
}

/**
 * Per-shot recoil impulse.
 * @returns {{pitch:number, yaw:number, vPitch:number, vYaw:number, vRoll:number, kick:number}}
 */
export function recoilImpulse(profile, shotIndex, seed) {
  const r = makeRng(shotHash(seed, shotIndex));
  const i = shotIndex;

  // --- vertical climb: fast ramp for the first few rounds, then a plateau ----
  const ramp = 1 - Math.exp(-i / (profile.rampShots ?? 2.4));
  const climb = profile.vertical * (profile.vertMin + (1 - profile.vertMin) * ramp);

  // --- horizontal walk: deterministic, weapon-specific, learnable ------------
  const p = profile.hPhase ?? 0;
  const walk =
    Math.sin(i * 0.62 + p) * 0.55 +
    Math.sin(i * 0.23 + p * 1.7) * 0.32 +
    Math.sin(i * 1.37 + p * 0.4) * 0.13;
  const drift = (profile.hDrift ?? 0) * Math.tanh(i / 8);
  const horiz = profile.horizontal * walk + profile.horizontal * drift;

  // --- small seeded random component ---------------------------------------
  const jv = r.gauss(0, profile.vertRandom ?? 0.06) * profile.vertical;
  const jh = r.gauss(0, profile.horizRandom ?? 0.30) * profile.horizontal;

  const pitch = climb + jv;
  const yaw = horiz + jh;

  return {
    pitch,
    yaw,
    vPitch: pitch * (profile.visualScale ?? 2.6),
    vYaw: yaw * (profile.visualScale ?? 2.6) * 0.75,
    vRoll: (yaw * (profile.visualScale ?? 2.6)) * 1.4 + r.range(-1, 1) * profile.vertical * 0.35,
    kick: profile.kick,
  };
}

/**
 * Recoil integrator. Owns the camera-side accumulation so recovery works even while
 * the player module is a stub: we push deltas through `addRecoil` and pull most of
 * them back as the spring settles.
 */
export class RecoilState {
  constructor() {
    this.shotIndex = 0;
    this.seed = 0x5eed;
    // aim (camera) recoil
    this.aimP = 0; this.aimY = 0;      // current desired offset
    this.aimVP = 0; this.aimVY = 0;    // velocities
    this.appliedP = 0; this.appliedY = 0;
    this.restP = 0; this.restY = 0;    // the part we never give back
    this.sinceShot = 10;
  }

  reset() {
    this.shotIndex = 0;
    this.aimP = this.aimY = this.aimVP = this.aimVY = 0;
    this.restP = this.restY = 0;
    this.sinceShot = 10;
  }

  fire(profile, seed) {
    const imp = recoilImpulse(profile, this.shotIndex, seed);
    this.shotIndex++;
    this.sinceShot = 0;
    this.aimVP += imp.pitch * (profile.snap ?? 34);
    this.aimVY += imp.yaw * (profile.snap ?? 34);
    // Only a fraction of the climb is ever handed back — this is what makes a spray
    // walk up the wall instead of returning to the same dot every time.
    this.restP += imp.pitch * (profile.retain ?? 0.22);
    this.restY += imp.yaw * (profile.retain ?? 0.22);
    return imp;
  }

  /** @returns {{dPitch:number, dYaw:number}} delta to hand to ctx.player.addRecoil */
  step(dt, profile) {
    this.sinceShot += dt;
    const w = profile.recoverOmega ?? 11;
    const tgtP = this.restP, tgtY = this.restY;
    this.aimVP += (-2 * w * this.aimVP - w * w * (this.aimP - tgtP)) * dt;
    this.aimVY += (-2 * w * this.aimVY - w * w * (this.aimY - tgtY)) * dt;
    this.aimP += this.aimVP * dt;
    this.aimY += this.aimVY * dt;

    // The retained portion bleeds off slowly once the trigger is released.
    if (this.sinceShot > (profile.settleDelay ?? 0.30)) {
      const k = Math.exp(-(profile.restDecay ?? 2.4) * dt);
      this.restP *= k; this.restY *= k;
    }

    const dP = this.aimP - this.appliedP;
    const dY = this.aimY - this.appliedY;
    this.appliedP = this.aimP;
    this.appliedY = this.aimY;
    return { dPitch: dP, dYaw: dY };
  }
}

/**
 * Bloom / spread. Hip-fire cone tightened by ADS and crouch, widened by movement and
 * sustained fire. Published on ctx.weapons.spread for the crosshair.
 */
export class SpreadState {
  constructor() {
    this.bloom = 0;
    this.value = 0;
  }

  reset() { this.bloom = 0; }

  fire(profile) {
    this.bloom = Math.min(profile.bloomMax ?? 1, this.bloom + (profile.bloomPerShot ?? 0.14));
  }

  /**
   * @param {number} dt
   * @param {object} profile
   * @param {{ads:number, speed:number, crouched:boolean, grounded:boolean, sprinting:boolean}} s
   */
  step(dt, profile, s) {
    this.bloom = Math.max(0, this.bloom - (profile.bloomDecay ?? 0.85) * dt);

    const hip = profile.spreadHip ?? 0.032;
    const ads = profile.spreadAds ?? 0.0022;
    let base = hip + (ads - hip) * s.ads;

    const moveT = clamp((s.speed ?? 0) / 4.6, 0, 1.4);
    base += (profile.spreadMove ?? 0.030) * moveT * (1 - 0.55 * s.ads);
    if (s.crouched) base *= (profile.spreadCrouch ?? 0.72);
    if (!s.grounded) base *= (profile.spreadAir ?? 2.1);
    if (s.sprinting) base *= 1.35;

    base += (profile.spreadBloom ?? 0.055) * this.bloom * (1 - 0.5 * s.ads);
    this.value = base;
    return base;
  }
}
